import React, { useMemo, useRef } from 'react';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import { Doughnut } from 'react-chartjs-2';
import 'chartjs-plugin-doughnutlabel';
import { FilterOption } from './MyTasksFilterDialog';
import { fixedLegendWidthPlugin } from './fixedLegendWidthPlugin';

interface Props {
  /** Every present option for this dimension (not yet limited to the top 6). */
  items: FilterOption[];
  /** Label for the grouped 7th+ slice, e.g. "Other Projects" / "Other Document Types". */
  otherLabel: string;
  /** Heading shown above the legend, e.g. "By Project" / "By Type". */
  title: string;
  /** Called with the value(s) a click should toggle — a single value for a real slice, every
   * grouped value at once for the "Other" slice. */
  onValueClick: (values: string[]) => void;
  /** Surface color behind the chart, matched to whatever this card is rendered on — the My Work
   * page's #FAFAFA canvas or the dashboard card's white one — so the chart reads as part of that
   * surface rather than as a tinted block sitting on it. */
  backgroundColor: string;
}

const TOP_N = 6;
// Distinct categorical palette (not status-semantic, since projects/doc types have no inherent
// color meaning) plus a neutral gray reserved for the grouped "Other" slice.
export const PALETTE = ['#0947B9', '#F28B00', '#2BB073', '#A25DDC', '#E2445C', '#4380ED'];
export const OTHER_COLOR = '#9E9E9E';

/**
 * Same top-6-by-count-plus-other color assignment this chart uses for its own slices, exposed so
 * other My Work page UI (the table's project pill) can color a value identically to its slice
 * here — one ranking, one palette, never able to disagree.
 */
export const getBreakdownColorsByValue = (items: FilterOption[]): Record<string, string> => {
  const sorted = [...items].sort((a, b) => b.count - a.count);
  const colors: Record<string, string> = {};
  sorted.forEach((item, i) => {
    colors[item.value] = i < TOP_N ? PALETTE[i % PALETTE.length] : OTHER_COLOR;
  });
  return colors;
};

const useStyles = makeStyles({
  root: { boxShadow: 'none' },
  flexColumn: {
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'nowrap',
  },
});

/**
 * Top-6-plus-other breakdown, sorted descending by count. Shared by the My Work "Tasks by
 * Project" (all-projects view) and "Tasks by Document Type" (single-project view) cards. The
 * doughnut/legend/click/hover behavior mirrors DashboardDoughnut exactly (same chart.js legend +
 * onClick + hover wiring) rather than a bespoke HTML legend.
 */
export default function MyTasksBreakdownChart({
  items,
  otherLabel,
  title,
  onValueClick,
  backgroundColor,
}: Props) {
  const classes = useStyles();
  const chartRef = useRef<Doughnut>(null);

  const { topItems, otherItems, otherTotal } = useMemo(() => {
    const sorted = [...items].sort((a, b) => b.count - a.count);
    return {
      topItems: sorted.slice(0, TOP_N),
      otherItems: sorted.slice(TOP_N),
      otherTotal: sorted.slice(TOP_N).reduce((sum, i) => sum + i.count, 0),
    };
  }, [items]);

  const slices = [
    ...topItems.map((item, i) => ({
      key: item.value,
      label: item.label,
      count: item.count,
      color: PALETTE[i % PALETTE.length],
      values: [item.value],
    })),
    ...(otherItems.length > 0
      ? [
          {
            key: '__other__',
            label: `${otherItems.length} ${otherLabel}`,
            count: otherTotal,
            color: OTHER_COLOR,
            values: otherItems.map((i) => i.value),
          },
        ]
      : []),
  ];

  const total = slices.reduce((sum, s) => sum + s.count, 0);

  /**
   * The legend truncates long names so one project cannot stretch the legend column, but the
   * tooltip always shows the full name -- truncating there would leave nowhere to read it.
   *
   * `slices` keeps the untruncated label; only what chart.js is handed is shortened.
   */
  const truncate = (label: string) => (label.length > 25 ? `${label.slice(0, 25)}...` : label);

  const pieData = {
    labels: slices.map((s) => `${truncate(s.label)} ${s.count}`),
    datasets: [
      {
        data: slices.map((s) => s.count),
        backgroundColor: slices.map((s) => s.color),
      },
    ],
  };

  const emptyData = {
    labels: ['No tasks'],
    datasets: [{ data: [1], backgroundColor: ['#EDECEC'] }],
  };

  const plugins = {
    doughnutlabel: {
      labels: [
        { text: total, font: { size: 44, lineHeight: '52px' } },
        { text: 'total', font: { size: 26, lineHeight: '30px' } },
      ],
    },
  };

  /**
   * Routed by slice index, not by label text. chart.js hands both the doughnut (`_index`) and the
   * legend (`index`) the position of the slice that was clicked, and `pieData` is built from
   * `slices` in order, so the index is an exact identity. Matching on the rendered label instead
   * would break the moment two of them collided -- which truncation makes possible: two projects
   * sharing their first 25 characters render the same legend text.
   */
  const handleSliceClick = (index: number) => {
    const slice = slices[index];
    if (slice) onValueClick(slice.values);
  };

  const doughnutOptions =
    total === 0
      ? {
          animation: { duration: 0 },
          maintainAspectRatio: false,
          responsive: true,
          plugins,
          events: [],
        }
      : {
          animation: { duration: 0 },
          maintainAspectRatio: false,
          responsive: true,
          plugins,
          tooltips: {
            callbacks: {
              // The untruncated name, which the legend may have shortened.
              label: (tooltipItem: { index: number }) => {
                const slice = slices[tooltipItem.index];
                return slice ? `${slice.label} ${slice.count}` : '';
              },
            },
          },
          hover: {
            onHover: (e: any, other: any) => {
              e.target.style.cursor = other.length ? 'pointer' : 'default';
            },
          },
          onClick: (e: any, items: any[]) => {
            if (items.length > 0) handleSliceClick(items[0]._index);
          },
        };

  const legendOptions = {
    onClick: (event: MouseEvent, item: { index: number }) => handleSliceClick(item.index),
    onHover: (e: any) => {
      e.target.style.cursor = 'pointer';
    },
  };

  return (
    <Card className={classes.root} style={{ backgroundColor }}>
      <CardContent className={classes.flexColumn} style={{ padding: '0px' }}>
        <Typography
          style={{ fontWeight: 600, fontSize: 16, color: '#464546', padding: '8px 8px 0' }}
        >
          {title}
        </Typography>
        <Grid container justify="flex-start" spacing={1} alignItems="stretch">
          {/* maxWidth matches MyTasksStatusSummary's chart column exactly, so both doughnuts
              respond to the same width:'99%' sizing and render identically — capped so the ring
              doesn't balloon on wide single-column (stacked) viewports. */}
          <Grid item xs={12} style={{ padding: 8, maxWidth: 560 }}>
            <Hidden xsDown>
              <div style={{ width: '99%' }}>
                <Doughnut
                  // Chart.js only binds DOM event listeners (click/hover/etc.) once, at
                  // construction, from whatever `options.events` is at that moment — a later
                  // options update (e.g. once `items` finishes loading) never re-binds them. Since
                  // this chart renders before `items` loads (total === 0, events disabled for the
                  // empty-state placeholder), keying on that transition forces a full remount — and
                  // a fresh Chart.js instance with real events — once data actually arrives, instead
                  // of leaving a permanently inert chart. See MyTasksStatusSummary's identical fix.
                  key={total === 0 ? 'empty' : 'data'}
                  ref={chartRef}
                  data={total === 0 ? emptyData : pieData}
                  width={300}
                  // Matches MyTasksStatusSummary's height — see its comment for why 300 left the
                  // ring surrounded by empty vertical space.
                  height={260}
                  legend={{ position: 'right', ...legendOptions }}
                  options={doughnutOptions}
                  plugins={[fixedLegendWidthPlugin]}
                />
              </div>
            </Hidden>
            <Hidden smUp>
              <div style={{ width: '99%' }}>
                <Doughnut
                  key={total === 0 ? 'empty' : 'data'}
                  data={total === 0 ? emptyData : pieData}
                  width={300}
                  height={300}
                  legend={{ position: 'bottom', ...legendOptions }}
                  options={doughnutOptions}
                />
              </div>
            </Hidden>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}
